import { zodResolver } from '@hookform/resolvers/zod';
import { JSX, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FileMimeTypes, FilePicker, ItemStatus } from '@/modules/common';

import { maxOverlayPreviewSize } from '../constants';
import { OverlayFormSchema, OverlayFormValues } from '../validations';

interface MaskFormProps {
  onSubmit: (data: OverlayFormValues) => Promise<void>;
  children: JSX.Element;
  editMode?: boolean;
  defaultValues?: OverlayFormValues & { imagePublicUrl: string };
}

const defaultFormValues = {
  name: '',
  status: '',
  imageUploadUrl: '',
  updatedAt: undefined,
};

export function OverlayForm({
  onSubmit,
  children,
  editMode,
  defaultValues,
}: MaskFormProps) {
  const form = useForm<OverlayFormValues>({
    resolver: zodResolver(OverlayFormSchema),
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {children}

        <div className="flex flex-col gap-10 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Overlay name</FormLabel>
                  <FormControl>
                    <Input placeholder="Add mask name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="updatedAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last modified</FormLabel>
                  <FormControl>
                    <Input
                      disabled
                      placeholder="YYYY-MM-DD"
                      required={false}
                      readOnly
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ItemStatus
              name="status"
              isDisabled={!editMode}
              control={form.control}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="col-span-2 flex flex-col gap-6">
              <h4 className="text-2xl font-semibold">Upload Overlay</h4>

              <FilePicker
                label="Overlay"
                name="imagePublicUrl"
                withFilePreview
                accept={[FileMimeTypes.Png]}
                placeholder="Upload Overlay"
                tips={
                  <p className="text-sm text-zinc-500">
                    The file must be in
                    <span className="text-zinc-100">&nbsp;.png&nbsp;</span>
                    format and maximum size 10 MB.
                  </p>
                }
                maxFileSize={maxOverlayPreviewSize}
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
